import { formatDistanceToNow, isAfter, isValid, parseISO, subDays } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'

/**
 * Determines if an asset is "active" based on location timestamp.
 * Active: Location timestamp is within the last 2 days.
 * Inactive: Location timestamp is older than 2 days or no location.
 * @param isoDateTime - ISO datetime string to check
 * @returns 'active' if within last 2 days, 'inactive' otherwise
 */
export const getAssetStatus = (isoDateTime: string | undefined): 'active' | 'inactive' => {
  if (!isoDateTime) return 'inactive'
  try {
    const locationDate = parseISO(isoDateTime)
    if (!isValid(locationDate)) return 'inactive'
    const twoDaysAgo = subDays(new Date(), 2)
    return isAfter(locationDate, twoDaysAgo) ? 'active' : 'inactive'
  } catch {
    return 'inactive'
  }
}

/**
 * Formats an ISO datetime string to user's local timezone in 'M/d h:mm a' format.
 * @param isoDateTime - ISO datetime string to format
 * @returns Formatted time string (e.g., "1/29 3:45 PM") or original string if invalid
 */
export const formatPreviewTime = (isoDateTime: string): string => {
  try {
    const parsedDate = parseISO(isoDateTime)
    if (!isValid(parsedDate)) return isoDateTime
    const userTz = Intl.DateTimeFormat().resolvedOptions().timeZone
    return formatInTimeZone(parsedDate, userTz, 'M/d h:mm a')
  } catch {
    return isoDateTime
  }
}

/**
 * Gets a human-readable relative time string from an ISO datetime.
 * Uses date-fns formatDistanceToNow for consistent formatting.
 * @param isoDateTime - ISO datetime string to convert
 * @returns Relative time string (e.g., "less than a minute ago", "5 minutes ago", "about 2 hours ago")
 */
export const getRelativeTime = (isoDateTime: string): string => {
  try {
    const parsedDate = parseISO(isoDateTime)
    if (!isValid(parsedDate)) return isoDateTime
    return formatDistanceToNow(parsedDate, { addSuffix: true })
  } catch {
    return formatPreviewTime(isoDateTime)
  }
}
